import "./App.css";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import Navbar from "./components/Navbar.jsx";
import Homepage from "./components/pages/Homepage.jsx";
import NotFound from "./components/NotFound.jsx";
import Contact from "./components/pages/Contact.jsx";
import Projects from "./components/pages/Projects.jsx";
import Privacy from "./components/pages/Privacy.jsx";
import Studio from "./components/pages/Studio.jsx";
import Footer from "./components/Footer.jsx";
import { ScrollTrigger, sunrise } from "./lib/motion";

/**
 * The shell.
 *
 * Atmosphere and chrome live outside the routed area so a page change never
 * remounts them: the grain must not flicker when you move between pages.
 *
 * All three atmosphere layers are static CSS. There was a canvas here that
 * drew a flow field and a turning wireframe; it cost a requestAnimationFrame
 * loop for the whole life of the page and, at the opacity a background has to
 * sit at, it read as speckle rather than as anything. Drafting lines do the
 * same job for nothing.
 */
function Shell({ children }) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    /* Every trigger on the page measured itself against the old document
       height. Refresh once the new route has painted, or a section further
       down releases at the wrong scroll position — or never releases at all. */
    const id = requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => cancelAnimationFrame(id);
  }, [pathname]);

  /**
   * The sunrise driver.
   *
   * It watches the attribute rather than taking the theme as a prop: the
   * toggle lives in the Navbar and writes `data-theme` straight onto <html>,
   * and threading that through React would mean re-rendering the whole tree
   * to move one number.
   *
   * Two conditions gate it. The homepage is the only route with a dark hero to
   * open on, and `chosen` latches the moment somebody presses the theme
   * button — after that the palette is their decision, not the scroll's.
   */
  const chosen = useRef(false);

  useEffect(() => {
    const root = document.documentElement;
    const theme = () => root.getAttribute("data-theme");
    const home = pathname === "/";

    let stop = sunrise(theme, { active: home && !chosen.current });

    const rebuild = () => {
      stop();
      stop = sunrise(theme, { active: home && !chosen.current });
    };

    const onChoice = () => {
      chosen.current = true;
      rebuild();
    };

    const observer = new MutationObserver(rebuild);
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    window.addEventListener("casper:theme-chosen", onChoice);

    return () => {
      observer.disconnect();
      window.removeEventListener("casper:theme-chosen", onChoice);
      stop();
    };
  }, [pathname]);

  return (
    <>
      <div className="beams" aria-hidden="true" />
      <div className="mesh" aria-hidden="true" />
      <div className="grain" aria-hidden="true" />

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:rounded-full focus:bg-fg focus:px-5 focus:py-3 focus:text-bg"
      >
        Naar de inhoud
      </a>

      <Navbar />

      {/* pt clears the fixed chrome: the top bar on a phone, the floating nav
          pill on desktop. pb clears the bottom tab bar. */}
      <div className="relative z-[2] mx-auto w-full max-w-[1180px] px-4 pt-[calc(74px+env(safe-area-inset-top))] pb-[calc(96px+env(safe-area-inset-bottom))] desk:px-10 desk:pt-[112px] desk:pb-10">
        <main id="main" tabIndex={-1} className="outline-none">
          {children}
        </main>
        <Footer />
      </div>
    </>
  );
}

/**
 * The local project editor is a development-only route.
 *
 * `process.env.NODE_ENV` is a literal at build time, so this whole branch —
 * and with it the import of Studio and everything it pulls in — is dropped by
 * the minifier from the production bundle. There is no /beheer on the live
 * site to find, and its endpoint (src/setupProxy.js) only ever exists inside
 * the dev server.
 */
const LOCAL = process.env.NODE_ENV === "development";

export default function App() {
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Homepage />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        {LOCAL && <Route path="/beheer" element={<Studio />} />}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Shell>
  );
}
